import { propertyManagerNav } from '@/components/sidebar/navigation/property-manager-nav';
import { companyAdminNav } from '@/components/sidebar/navigation/company-admin-nav';
import { homeOwnerNav } from '@/components/sidebar/navigation/home-owner-nav';
import { residentNav } from '@/components/sidebar/navigation/resident-nav';
import { sharedNav } from '@/components/sidebar/navigation/shared-nav';
import { utilityAdminNav } from '@/components/sidebar/navigation/utility-admin-nav';
import { accountPayablesNav } from '@/components/sidebar/navigation/account-payables-nav';
import { accountReceivablesNav } from '@/components/sidebar/navigation/account-receivables-nav';
import { accountantNav } from '@/components/sidebar/navigation/accountant-nav';
import { Role } from '@/api/auth';
import { cacheConfigs } from '@/lib/cache_helpers';
import { useAuthStore } from '@/store/authStore';

import { API_BASE_URL } from "@/lib/config/api-base-url";

/**
 * Checks if a response contains meaningful data worth caching
 * @param data The response data to check
 * @returns boolean indicating if the response has meaningful data
 */
const isResponseMeaningful = (data: any): boolean => {
    // Check for standard paginated responses
    if (data && typeof data === 'object') {
        // Check for results array
        if (Array.isArray(data.results)) {
            if (data.results.length === 0) {
                return false; // Skip empty results arrays
            }
            return true; // Has items in results array
        }

        // Check for service_account_trends (bill trends)
        if (Array.isArray(data.service_account_trends)) {
            if (data.service_account_trends.length === 0) {
                return false; // Skip empty service_account_trends arrays
            }
            return true; // Has items in service_account_trends array
        }

        // Check for accounts array (bank accounts)
        if (Array.isArray(data.accounts)) {
            if (data.accounts.length === 0) {
                return false; // Skip empty accounts arrays
            }
            return true; // Has items in accounts array
        }

        // Check for bills array (spike alerts)
        if (Array.isArray(data.bills)) {
            if (data.bills.length === 0) {
                return false; // Skip empty bills arrays
            }
            return true; // Has items in bills array
        }

        // Check for properties array
        if (Array.isArray(data.properties)) {
            if (data.properties.length === 0) {
                return false; // Skip empty properties arrays
            }
            return true; // Has items in properties array
        }

        // Check for hoaRefunds array
        if (Array.isArray(data.hoaRefunds)) {
            if (data.hoaRefunds.length === 0) {
                return false; // Skip empty hoaRefunds arrays
            }
            return true; // Has items in hoaRefunds array
        }

        // If it's just an empty object with no useful data
        if (Object.keys(data).length === 0) {
            return false; // Skip empty objects
        }

        // If it's an object but not one of the standard array responses,
        // we'll consider it meaningful unless it's specifically shaped like an empty response
        if (
            data.count === 0 &&
            (!data.results || data.results.length === 0) &&
            !data.next &&
            !data.previous
        ) {
            return false; // Skip empty paginated responses
        }
    }

    // Default to true for any other response
    return true;
};

// React Query cache keys by page path - matches actual cache keys used by each page
const getReactQueryCacheKeys = (companyId: string, dateRange: { from: string; to: string }) => {
    return {
        // ===== PROPERTY MANAGER PAGES =====
        '/dashboard/company/accounting-statements': [
            JSON.stringify(['company', 'payments', { limit: 100, page: 1, timeframe: 'custom', customDateRange: dateRange }])
        ],
        '/dashboard/transactions/pending-and-cleared': [
            JSON.stringify(['transactions', { limit: 10, page: 1, timeframe: 'this-month' }])
        ],
        '/dashboard/reports/missing-services': [
            JSON.stringify(['missingServicesStats'])
        ],
        '/dashboard/settings/company': [
            JSON.stringify(['company', 'info', companyId])
        ],
        '/dashboard/reports/service-trends': [
            JSON.stringify(['bill-trends', { limit: 10, page: 1 }])
        ],
        '/dashboard/reports/statements': [
            JSON.stringify(['company', 'statements', { limit: 10000, timeframe: 'custom', customDateRange: dateRange }])
        ],
        '/dashboard/transactions/declined': [
            JSON.stringify(['transactions', 'declined', { limit: 100, page: 1, timeframe: 'this-month' }])
        ],
        '/dashboard/reports/active-services': [
            JSON.stringify(['activeBillsStats'])
        ],
        '/dashboard/settings/bank': [
            JSON.stringify(['bank', 'accounts'])
        ],
        '/dashboard/reports/spikes': [
            JSON.stringify(['spike-alerts', { timeframe: 'custom', customDateRange: dateRange }])
        ],
        '/dashboard/settings/spike-alert': [
            JSON.stringify(['settings', 'spike-alerts'])
        ],
        '/dashboard/company/insights': [
            JSON.stringify(['company', 'portfolios', companyId]),
            JSON.stringify(['company', 'insights', 'spend_over_time', { timeframe: 'last-12-months', compareEnabled: false }]),
            JSON.stringify(['company', 'insights', 'spend_breakdown', { timeframe: 'last-12-months', type: 'merchants' }]),
            JSON.stringify(['company', 'insights', 'spend_breakdown', { timeframe: 'last-12-months', type: 'category' }])
        ],
        '/dashboard/settings/members': [
            JSON.stringify(['company', 'users', { companyId }]),
            JSON.stringify(['properties'])
        ],
        '/dashboard/transactions/fees': [
            JSON.stringify(['transactions', 'fees', { limit: 10, page: 1, sortBy: 'date', sortOrder: 'desc', timeframe: 'this-month' }])
        ],
        '/dashboard/transactions/adjustments': [
            JSON.stringify(['transactions', 'adjustments', { limit: 10, page: 1, sortBy: 'due_date', sortOrder: 'desc', timeframe: 'this-month' }])
        ],
        '/dashboard/reports/bill-back': [
            JSON.stringify(['company', 'billback', { limit: 10000, timeframe: 'custom', customDateRange: dateRange }])
        ],
        '/dashboard/reports/bill-pay': [
            JSON.stringify(['company', 'billpay', { limit: 10000, timeframe: 'custom', customDateRange: dateRange }])
        ],
        '/dashboard/company/statements': [
            JSON.stringify(['company', 'accounting-statements', { page: 1, pageSize: 10, timeframe: 'this-month' }])
        ],
        '/dashboard/properties': [
            JSON.stringify(['propertyStats'])
        ],
        '/dashboard/properties/manage-services': [
            JSON.stringify(['services', { limit: 10, page: 1, sort_by: 'biller_name', sort_order: 'asc' }])
        ],
        '/dashboard/properties/bulk-buy': [
            JSON.stringify(['bulk-buy', { limit: 50, page: 1, sortBy: 'yearly_savings', sortOrder: 'desc' }]),
            JSON.stringify(['service-providers', 'cable']),
            JSON.stringify(['service-providers', 'internet']),
            JSON.stringify(['service-providers', 'electricity'])
        ],
        '/dashboard/properties/deposits': [
            JSON.stringify(['deposits'])
        ],

        // ===== PROPERTY MANAGER PAGES =====
        // Property Manager now uses (shared) pages - no separate cache keys needed
        // Dashboard pages:
        '/dashboard/property-manager/dashboard/overview': [],

        // ===== COMPANY ADMIN PAGES =====
        // Company Admin now uses (shared) pages - no separate cache keys needed
        // Dashboard pages:
        '/dashboard/company-admin/dashboard/overview': [],

        // ===== UTILITY ADMIN PAGES =====
        '/dashboard/utility-admin/dashboard/overview': [
            JSON.stringify(['utility-admin-dashboard'])
        ],
        '/dashboard/utility-admin/manage/properties': [
            JSON.stringify(['propertyStats'])
        ],
        '/dashboard/utility-admin/manage/lockbox': [
            JSON.stringify(['tickets', { ticket_category: 'Lockbox Code' }])
        ],
        '/dashboard/utility-admin/manage/trash-bins': [
            JSON.stringify(['utility-admin', 'trash-bins'])
        ],
        '/dashboard/utility-admin/manage/documents': [
            JSON.stringify(['utility-admin', 'documents'])
        ],
        '/dashboard/utility-admin/work-orders/acquisitions': [
            JSON.stringify(['tickets', { ticket_category: 'Activation Service On' }])
        ],
        '/dashboard/utility-admin/work-orders/activate-service': [
            JSON.stringify(['tickets', { ticket_category: 'Activation Service On' }])
        ],
        '/dashboard/utility-admin/work-orders/deactivate-service': [
            JSON.stringify(['tickets', { ticket_category: 'Deactivation Service Off' }])
        ],
        '/dashboard/utility-admin/work-orders/trash-requests': [
            JSON.stringify(['tickets', { ticket_category: 'Trash Request' }])
        ],
        '/dashboard/utility-admin/work-orders/disconnects': [
            JSON.stringify(['tickets', { ticket_category: 'Disconnect' }])
        ],
        '/dashboard/utility-admin/work-orders/final-bills': [
            JSON.stringify(['tickets', { ticket_category: 'Final Bill' }])
        ],
        '/dashboard/utility-admin/work-orders/leak-refunds': [
            JSON.stringify(['tickets', { ticket_category: 'High Bill' }])
        ],
        '/dashboard/utility-admin/work-orders/other': [
            JSON.stringify(['tickets', { ticket_category: 'other' }])
        ],
        '/dashboard/utility-admin/reports/property-utility-audit': [
            JSON.stringify(['missingServicesStats'])
        ],
        '/dashboard/utility-admin/reports/active-bills': [
            JSON.stringify(['activeBillsStats'])
        ],
        '/dashboard/utility-admin/reports/spikes': [
            JSON.stringify(['utility-admin', 'spikes'])
        ],
        '/dashboard/utility-admin/reports/all-fees': [
            JSON.stringify(['transaction-fees', 'all', 'last-12-months', null])
        ],
        '/dashboard/utility-admin/reports/markets': [
            JSON.stringify(['utility-admin', 'markets'])
        ],
        '/dashboard/utility-admin/reports/missed-bills': [
            JSON.stringify(['account-payables', 'missed-bills'])
        ],
        '/dashboard/utility-admin/reports/arrears': [
            JSON.stringify(['arrearsStats'])
        ],
        '/dashboard/utility-admin/settings/thresholds': [
            JSON.stringify(['utility-admin', 'thresholds'])
        ],
        '/dashboard/utility-admin/settings/notifications': [
            JSON.stringify(['utility-admin', 'notifications'])
        ],

        // ===== ACCOUNT PAYABLES PAGES =====
        '/dashboard/account-payables/dashboard/overview': [
            JSON.stringify(['account-payables', 'dashboard', '30d'])
        ],
        '/dashboard/account-payables/dashboard/missed-bills': [
            JSON.stringify(['account-payables', 'missed-bills'])
        ],
        '/dashboard/account-payables/payables/upcoming': [
            JSON.stringify(['account-payables', 'flis', 'upcoming', {
                page: 1,
                limit: 25,
                sortBy: 'created_at',
                sortOrder: 'desc',
            }])
        ],
        '/dashboard/account-payables/payables/disbursed': [
            JSON.stringify(['account-payables', 'flis', 'disbursed', {
                page: 1,
                limit: 25,
                sortBy: 'updated_at',
                sortOrder: 'desc',
            }])
        ],
        '/dashboard/account-payables/payables/flagged': [
            JSON.stringify(['account-payables', 'flis', 'flagged', {
                page: 1,
                limit: 25,
                sortBy: 'flagged_date',
                sortOrder: 'desc',
            }])
        ],
        '/dashboard/account-payables/reports/monthly-payables': [
            JSON.stringify(['account-payables', 'statements', {
                page: 1,
                limit: 10,
                sort: 'month',
                sortOrder: 'desc'
            }])
        ],
        '/dashboard/company/funding-history': [
            JSON.stringify(['account-payables', 'payments', {
                page: 1,
                limit: 10,
                sort: 'date',
                sortOrder: 'desc'
            }])
        ],
        '/dashboard/account-payables/reports/fees-absorption': [
            JSON.stringify(['transaction-fees', 'fees-absorption', 'last-12-months', null])
        ],
        '/dashboard/account-payables/settings/bank-accounts': [
            JSON.stringify(['account-payables', 'bank-accounts'])
        ],
        '/dashboard/account-payables/settings/gl-codes': [
            JSON.stringify(['account-payables', 'gl-codes'])
        ],
        '/dashboard/account-payables/settings/spike-threshold': [
            JSON.stringify(['account-payables', 'spike-threshold'])
        ],

        // ===== ACCOUNT RECEIVABLES PAGES =====
        '/dashboard/account-receivables/dashboard/overview': [
            JSON.stringify(['yardi-tenant-charges', 'all'])
        ],
        '/dashboard/account-receivables/receivables/draft': [
            JSON.stringify(['yardi-tenant-charges', 'non-approved'])
        ],
        '/dashboard/account-receivables/receivables/approved': [
            JSON.stringify(['yardi-tenant-charges', 'approved'])
        ],
        '/dashboard/account-receivables/receivables/flagged': [
            JSON.stringify(['yardi-tenant-charges', 'flagged'])
        ],
        '/dashboard/account-receivables/reports/tenant-charges': [
            JSON.stringify(['account-receivables', 'bill-back'])
        ],

        // ===== RESIDENT PAGES =====
        '/dashboard/': [
            JSON.stringify(['resident', 'dashboard'])
        ],
        '/dashboard/resident/home/billers': [
            JSON.stringify(['resident', 'billers'])
        ],
        '/dashboard/resident/home/work-orders': [
            JSON.stringify(['tickets', { ticket_category: 'Disconnect,Trash Request,Technical Support,Other' }])
        ],
        '/dashboard/reports/statements': [
            JSON.stringify(['resident', 'monthly-statements'])
        ],
        '/dashboard/company/accounting-statements': [
            JSON.stringify(['resident', 'payments'])
        ],
        '/dashboard/transactions/pending-and-cleared': [
            JSON.stringify(['resident', 'pending-cleared'])
        ],
        '/dashboard/transactions/declined': [
            JSON.stringify(['resident', 'declined'])
        ],
        '/dashboard/settings/spike-alert': [
            JSON.stringify(['settings', 'spike-alert'])
        ],
        '/dashboard/settings/payments': [
            JSON.stringify(['settings', 'payments', 'bank-accounts']),
            JSON.stringify(['settings', 'payments', 'service-accounts'])
        ],
        '/dashboard/settings/notifications': [
            JSON.stringify(['settings', 'notifications'])
        ]
    };
};

/**
 * Get the start and end dates of the current month
 * @returns Object with from (start date) and to (end date) of current month
 */
const getCurrentMonthDateRange = (): { from: string; to: string } => {
    const now = new Date();

    // Start of current month (1st day)
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

    // End of current month (last day)
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Format dates as YYYY-MM-DD
    const formatDate = (date: Date): string => {
        return date.toISOString().split('T')[0];
    };

    return {
        from: formatDate(startDate),
        to: formatDate(endDate)
    };
};

// Get navigation items based on user role
const getNavItemsByRole = (role: Role) => {
    switch (role) {
        case 'admin':
            // Admin gets all navigation items - combine all role navs
            return [
                ...propertyManagerNav,
                ...companyAdminNav,
                ...utilityAdminNav,
                ...accountPayablesNav,
                ...accountReceivablesNav
            ];
        case 'property_manager':
            return propertyManagerNav;
        case 'company_admin':
            return companyAdminNav;
        case 'utility_admin':
            return utilityAdminNav;
        case 'account_payables':
            return accountPayablesNav;
        case 'account_receivables':
            return accountReceivablesNav;
        case 'accountant':
            return accountantNav;
        case 'home_owner':
            return homeOwnerNav;
        case 'tenant':
        case 'resident':
            return residentNav;
        default:
            return [];
    }
};

// Get all URLs from navigation items
const getAllUrlsFromNav = (navItems: any[]) => {
    const urls: string[] = [];

    navItems.forEach(item => {
        if (item.url) {
            urls.push(item.url);
        }

        if (item.items && Array.isArray(item.items)) {
            item.items.forEach((subItem: any) => {
                if (subItem.url) {
                    urls.push(subItem.url);
                }
            });
        }
    });

    return urls;
};

/**
 * Preloads API data for the user's role in the background using React Query
 * @param role The user's role
 */
export const preloadApiDataByRole = async (role: Role) => {
    // Check if preloading has already happened in this session
    // This prevents multiple preloads during development when files change
    const hasPreloaded = sessionStorage.getItem('api_preload_completed');
    if (hasPreloaded === 'true') {
        console.log('API preloading already completed for this session, skipping...');
        return;
    }

    if (process.env.NODE_ENV === 'development') {
        console.log(`Starting API preloading for role: ${role}`);
    }

    try {
        // Get the company ID using the auth store or fallback to localStorage
        const getCompanyId = useAuthStore.getState().getCompanyId;
        let companyId = getCompanyId();

        // If no company ID from auth store, try localStorage
        if (!companyId) {
            companyId = localStorage.getItem('company_id') || '';
            console.log(`Using company ID from localStorage: ${companyId}`);
        }

        // If still no company ID, log warning and use empty string
        if (!companyId) {
            console.warn('No company ID available for preloading, some cache keys may be incomplete');
            companyId = '';
        } else {
            console.log(`Using company ID for preloading: ${companyId}`);
        }

        // Get navigation items for the user's role
        const navItems = getNavItemsByRole(role);

        // Add shared nav items
        const allNavItems = [...navItems, ...sharedNav];

        // Get all URLs from navigation items
        const urls = getAllUrlsFromNav(allNavItems);
        if (process.env.NODE_ENV === 'development') {
            console.log(`Found ${urls.length} navigation URLs to preload data for`);
        }

        // Get current month date range
        const dateRange = getCurrentMonthDateRange();
        if (process.env.NODE_ENV === 'development') {
            console.log(`Using date range for preloading: ${dateRange.from} to ${dateRange.to}`);
        }

        // Get React Query cache keys mapping for the user's company
        const cacheKeysMap = getReactQueryCacheKeys(companyId, dateRange);

        // Track results for logging
        const preloadedCaches: string[] = [];
        const failedCaches: string[] = [];

        // Start preloading after a short delay to allow React Query to initialize
        setTimeout(async () => {
            // Import React Query client dynamically to avoid SSR issues
            let queryClient;
            try {
                const { queryClient: qc } = await import('@/lib/global-cache-provider');
                queryClient = qc;

                // Verify queryClient is available
                if (!queryClient) {
                    console.warn('React Query client not available for preloading');
                    return;
                }
            } catch (error) {
                console.warn('Could not access React Query client for preloading:', error);
                return;
            }

            // Collect all prefetch promises
            const prefetchPromises = [];

            // Process each URL from navigation
            for (const url of urls) {
                // Set the current page context for this URL - extract clean page name
                currentPreloadPageContext = getCleanPageName(url);

                // Check if we have cache keys for this page
                const cacheKeys = cacheKeysMap[url];
                if (cacheKeys && cacheKeys.length > 0) {
                    for (const cacheKeyString of cacheKeys) {
                        // Parse the cache key back to array format
                        try {
                            const queryKey = JSON.parse(cacheKeyString);

                            // Create a prefetch function for this specific query
                            const prefetchQuery = async () => {
                                try {
                                    if (process.env.NODE_ENV === 'development') {
                                        console.log(`Prefetching data for ${url} with key:`, queryKey);
                                    }

                                    // VAPT #15: auth rides as an HttpOnly cookie now (the
                                    // global fetch patch injects credentials for API_BASE_URL
                                    // calls), so there is no localStorage token to read/gate on.
                                    const queryFn = await getQueryFunction(queryKey);

                                    if (queryFn) {
                                        // Use React Query's prefetchQuery to cache the data
                                        // Only cache successful responses
                                        const result = await queryFn();

                                        // Check if the response is meaningful before caching
                                        if (isResponseMeaningful(result)) {
                                            await queryClient.prefetchQuery({
                                                queryKey,
                                                queryFn: () => Promise.resolve(result),
                                                staleTime: 5 * 60 * 1000, // 5 minutes
                                                gcTime: 10 * 60 * 1000, // 10 minutes
                                            });
                                        } else {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.log(`⏭️ Skipping cache for ${url} - empty/meaningless response`);
                                            }
                                            return; // Skip caching empty responses
                                        }

                                        preloadedCaches.push(cacheKeyString);
                                        if (process.env.NODE_ENV === 'development') {
                                            console.log(`✅ Successfully preloaded cache for ${url}`);
                                        }
                                    } else {
                                        if (process.env.NODE_ENV === 'development') {
                                            console.log(`⚠️ No query function found for ${url} with key:`, queryKey);
                                        }
                                        failedCaches.push(cacheKeyString);
                                    }
                                } catch (err) {
                                    // Check if it's a 404 or other HTTP error that shouldn't be cached
                                    if (err instanceof Error) {
                                        const errorMessage = err.message.toLowerCase();

                                        // Skip 404 errors silently
                                        if (errorMessage.includes('404') ||
                                            errorMessage.includes('not found') ||
                                            errorMessage.includes('failed with status 404')) {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.log(`⚠️ API not found (404) for ${url} - skipping cache`);
                                            }
                                            return;
                                        }

                                        // Skip authentication/authorization errors silently
                                        if (errorMessage.includes('401') ||
                                            errorMessage.includes('403') ||
                                            errorMessage.includes('unauthorized') ||
                                            errorMessage.includes('forbidden') ||
                                            errorMessage.includes('user not found') ||
                                            errorMessage.includes('not authenticated') ||
                                            errorMessage.includes('failed with status 401') ||
                                            errorMessage.includes('failed with status 403')) {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.log(`🔒 Authentication/Authorization error for ${url} - skipping cache`);
                                            }
                                            return;
                                        }

                                        // Skip server errors silently
                                        if (errorMessage.includes('500') ||
                                            errorMessage.includes('502') ||
                                            errorMessage.includes('503') ||
                                            errorMessage.includes('failed with status 5')) {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.log(`🚫 Server error for ${url} - skipping cache`);
                                            }
                                            return;
                                        }
                                    }

                                    console.error(`❌ Failed to preload cache for ${url}:`, err);
                                    failedCaches.push(cacheKeyString);
                                }
                            };

                            // Add this prefetch operation to our list of promises
                            prefetchPromises.push(prefetchQuery());
                        } catch (parseError) {
                            console.error(`Failed to parse cache key for ${url}:`, parseError);
                        }
                    }
                } else {
                    console.log(`No cache keys defined for page: ${url}`);
                }
            }

            // Wait for all prefetches to complete (in parallel)
            await Promise.allSettled(prefetchPromises);

            // Clear the page context after preloading is complete
            currentPreloadPageContext = null;

            console.log(`API preloading completed for role: ${role}`);
            console.log(`Successfully preloaded ${preloadedCaches.length} cache entries`);
            if (failedCaches.length > 0) {
                console.warn(`Failed to preload ${failedCaches.length} cache entries`);
            }

            // Mark preloading as completed for this session
            sessionStorage.setItem('api_preload_completed', 'true');

        }, 2000); // 2 second delay before starting

    } catch (error) {
        // Catch any errors but don't interrupt the user experience
        console.error('Error in API preloading:', error);
    }
};

// Global variable to track current page context during preloading
let currentPreloadPageContext: string | null = null;

/**
 * Get the current page context for cache key differentiation
 */
function getCurrentPageContext(): string | null {
    return currentPreloadPageContext;
}

/**
 * Extract clean page name from URL for professional cache keys
 */
function getCleanPageName(url: string): string {
    // Map specific utility-admin URLs to clean names
    const pageNameMap: Record<string, string> = {
        '/dashboard/utility-admin/work-orders/disconnects': 'disconnects',
        '/dashboard/utility-admin/work-orders/final-bills': 'final_bills',
        '/dashboard/utility-admin/work-orders/leak-refunds': 'leak_refunds',
        '/dashboard/utility-admin/work-orders/trash-requests': 'trash_requests',
        '/dashboard/utility-admin/work-orders/acquisitions': 'acquisitions',
        '/dashboard/utility-admin/work-orders/activate-service': 'activate',
        '/dashboard/utility-admin/work-orders/deactivate-service': 'deactivate',
        '/dashboard/utility-admin/work-orders/other': 'other',
        '/dashboard/utility-admin/manage/lockbox': 'lockbox',
        '/dashboard/utility-admin/manage/trash-bins': 'trash_bins',
        '/dashboard/utility-admin/reports/all-fees': 'all_fees',
        '/dashboard/utility-admin/reports/missed-bills': 'missed_bills',
        '/dashboard/resident/home/billers': 'resident_billers',
        '/dashboard/resident/home/work-orders': 'resident_work_orders',
    };

    return pageNameMap[url] || url.replace('/dashboard/', '').replace(/\//g, '-');
}

/**
 * Maps React Query cache keys to their corresponding API functions
 * @param queryKey The React Query cache key array
 * @returns The appropriate query function or null
 */
async function getQueryFunction(queryKey: any[]) {
    const [firstKey, secondKey, params] = queryKey;

    try {
        // Handle different API patterns based on the cache key structure
        switch (firstKey) {
            case 'utility-admin-dashboard':
                return async () => {
                    const response = await fetch(`${API_BASE_URL}/api/v1/utility-admin/dashboard`, {
                        credentials: 'include', headers: { 'Content-Type': 'application/json' }
                    });
                    if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                    const result = await response.json();
                    // Handle success wrapper format
                    return result.success ? result.data : result;
                };

            case 'tickets':
                const { listTickets } = await import('@/api/tickets');
                // Extract page context from the current preload URL to prevent cache collisions
                const pageContext = getCurrentPageContext();
                return () => listTickets(secondKey || {}, pageContext);

            case 'propertyStats':
                const { listProperties } = await import('@/api/properties/manage_homes');
                return async () => {
                    try {
                        return await listProperties();
                    } catch (error) {
                        // If properties API fails, return empty response instead of throwing
                        if (error instanceof Error && error.message.includes('404')) {
                            return {
                                properties: [],
                                statistics: {
                                    totalProperties: 0,
                                    byStatus: { active: 0, inactive: 0, pending: 0 },
                                    byType: { single_family: 0, multi_family: 0, apartment: 0 }
                                },
                                pagination: {
                                    page: 1,
                                    limit: 10,
                                    totalPages: 0,
                                    totalRecords: 0
                                }
                            };
                        }
                        throw error;
                    }
                };

            case 'missingServicesStats':
                const { getPropertyUtilityAudit } = await import('@/api/reports/missing_services');
                return async () => {
                    try {
                        return await getPropertyUtilityAudit();
                    } catch (error) {
                        // If missing services API fails, return empty response instead of throwing
                        if (error instanceof Error && error.message.includes('404')) {
                            return {
                                properties: [],
                                filters: {
                                    utilityTypes: ['electricity', 'water', 'gas', 'cable', 'internet', 'security', 'insurance', 'hoa'],
                                    statusTypes: ['Submitted', 'Missing', 'Pending']
                                }
                            };
                        }
                        throw error;
                    }
                };

            case 'activeBillsStats':
                const { listActiveBills } = await import('@/api/bills/active-bills');
                return async () => {
                    try {
                        return await listActiveBills();
                    } catch (error) {
                        // If active bills API fails, return empty response instead of throwing
                        if (error instanceof Error && error.message.includes('404')) {
                            return {
                                active_services: [],
                                metadata: {
                                    total_properties: 0,
                                    total_service_accounts: 0,
                                    total_pending_bills: 0
                                }
                            };
                        }
                        throw error;
                    }
                };

            case 'arrearsStats':
                const { getArrears } = await import('@/api/reports/arrears');
                return () => getArrears();

            case 'account-payables':
                if (secondKey === 'dashboard') {
                    const { fetchDashboardStats } = await import('@/api/account-payables');
                    return () => fetchDashboardStats(params || '30d');
                } else if (secondKey === 'bills-pending') {
                    const { fetchPendingBills } = await import('@/api/account-payables');
                    return () => fetchPendingBills(params || {});
                } else if (secondKey === 'bills-approved') {
                    const { fetchApprovedBills } = await import('@/api/account-payables');
                    return () => fetchApprovedBills(params || {});
                } else if (secondKey === 'payments') {
                    const { fetchPayments } = await import('@/api/account-payables');
                    return () => fetchPayments(params || {});
                } else if (secondKey === 'bank-accounts') {
                    const { fetchBankAccounts } = await import('@/api/account-payables');
                    return () => fetchBankAccounts();
                } else if (secondKey === 'gl-codes') {
                    const { fetchGLCodes } = await import('@/api/account-payables');
                    return () => fetchGLCodes({ type: 'Bill Type' });
                } else if (secondKey === 'statements') {
                    const { fetchStatements } = await import('@/api/account-payables');
                    return () => fetchStatements(params || {});
                } else if (secondKey === 'missed-bills') {
                    const { getMissingBills } = await import('@/api/reports/missing_accounts');
                    return () => getMissingBills(params || {});
                } else if (secondKey === 'spike-threshold') {
                    const { getSpikeAlertSettings: getPayablesSpikeThreshold } = await import('@/api/settings/spike-alerts');
                    return () => getPayablesSpikeThreshold();
                }
                break;

            case 'yardi-tenant-charges':
                return async () => {
                    let endpoint = `${API_BASE_URL}/api/v1/yardi-tenant-charges/`;

                    if (secondKey === 'non-approved') {
                        endpoint = `${API_BASE_URL}/api/v1/yardi-tenant-charges/non-approved/`;
                    } else if (secondKey === 'approved') {
                        endpoint = `${API_BASE_URL}/api/v1/yardi-tenant-charges/?approved=true`;
                    } else if (secondKey === 'flagged') {
                        endpoint = `${API_BASE_URL}/api/v1/yardi-tenant-charges/?flagged=true`;
                    }

                    const response = await fetch(endpoint, {
                        credentials: 'include', headers: { 'Content-Type': 'application/json' }
                    });

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        const errorMessage = errorData?.detail || errorData?.message || errorData?.error || `API request failed with status ${response.status}`;
                        throw new Error(errorMessage);
                    }

                    return response.json();
                };

            case 'account-receivables':
                if (secondKey === 'bill-back') {
                    const { listBillBackItems: fetchReceivablesBillBack } = await import('@/api/bills/billback');
                    return () => fetchReceivablesBillBack(params || {});
                }
                break;

            case 'company':
                if (secondKey === 'payments') {
                    const { listCompanyPayments } = await import('@/api/company/payments');
                    return () => listCompanyPayments(params || {});
                } else if (secondKey === 'info') {
                    // params should be the companyId string directly
                    const companyId = typeof params === 'string' ? params : (params?.companyId || params?.id || '');
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/companies/${companyId}/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'statements') {
                    const { listCompanyStatements } = await import('@/api/bills/statements');
                    return () => listCompanyStatements(params || {});
                } else if (secondKey === 'billback') {
                    const { listBillBackItems } = await import('@/api/bills/billback');
                    return () => listBillBackItems(params || {});
                } else if (secondKey === 'billpay') {
                    const { listBillPayments } = await import('@/api/bills/billpay');
                    return () => listBillPayments(params || {});
                } else if (secondKey === 'accounting-statements') {
                    const { listAccountingStatements } = await import('@/api/company/accounting_statements');
                    return () => listAccountingStatements(params || {});
                } else if (secondKey === 'portfolios') {
                    const { getCompanyPortfolios } = await import('@/api/properties/manage_homes');
                    // params should be the companyId string directly
                    return () => getCompanyPortfolios(typeof params === 'string' ? params : params?.companyId || '');
                } else if (secondKey === 'insights') {
                    const thirdKey = params;
                    const { getSpendOverTime, getSpendBreakdown } = await import('@/api/company/insights');
                    if (thirdKey === 'spend_over_time') {
                        return () => getSpendOverTime(queryKey[3] || {});
                    } else if (thirdKey === 'spend_breakdown') {
                        return () => getSpendBreakdown(queryKey[3] || {});
                    }
                } else if (secondKey === 'users') {
                    const { getCompanyUsers } = await import('@/api/settings/users');
                    // params should be the companyId string directly
                    return () => getCompanyUsers(typeof params === 'string' ? params : params?.companyId || '');
                }
                break;

            case 'transaction-fees':
                const { listTransactionFees } = await import('@/api/transactions/fees');
                // secondKey is page context (e.g., 'fees-absorption', 'all')
                // params is the timeframe
                // queryKey[3] is customDateRange
                const timeframe = params || 'last-12-months';
                const customDateRange = queryKey[3];
                return () => listTransactionFees({
                    limit: 10000,
                    timeframe,
                    ...(customDateRange && { customDateRange })
                });

            case 'transactions':
                if (secondKey === 'declined') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/transactions/declined/?${new URLSearchParams(params)}`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'fees') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/reports/v1/transaction-fees/?${new URLSearchParams(params)}`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'adjustments') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/transactions/adjustments/?${new URLSearchParams(params)}`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/transactions/?${new URLSearchParams(params)}`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                }
                break;

            case 'bill-trends':
                const { getBillTrends } = await import('@/api/reports/bill_trends');
                return () => getBillTrends(secondKey || {});

            case 'bank':
                if (secondKey === 'accounts') {
                    const { listBankAccounts } = await import('@/api/company/bank');
                    return () => listBankAccounts();
                }
                break;

            case 'spike-alerts':
                const { getSpikeAlerts } = await import('@/api/reports/spike_alerts');
                return () => getSpikeAlerts(secondKey || {});

            case 'settings':
                if (secondKey === 'spike-alerts') {
                    const { getSpikeAlertSettings } = await import('@/api/settings/spike-alerts');
                    return () => getSpikeAlertSettings();
                } else if (secondKey === 'spike-alert') {
                    const { getSpikeAlertSettings } = await import('@/api/settings/spike-alerts');
                    return () => getSpikeAlertSettings();
                } else if (secondKey === 'payments') {
                    const thirdKey = params as string;
                    if (thirdKey === 'bank-accounts') {
                        const { listBankAccounts } = await import('@/api/company/bank');
                        return () => listBankAccounts();
                    } else if (thirdKey === 'service-accounts') {
                        const { getServiceAccountsForUser } = await import('@/api/properties/manage_services');
                        return () => getServiceAccountsForUser();
                    }
                } else if (secondKey === 'notifications') {
                    const { getNotificationSettings } = await import('@/api/settings/notifications');
                    return () => getNotificationSettings();
                }
                break;

            case 'services':
                const { listServices } = await import('@/api/properties/manage_services');
                return () => listServices(secondKey || {});

            case 'bulk-buy':
                const { listBulkBuyOpportunities } = await import('@/api/properties/bulk_buy');
                return () => listBulkBuyOpportunities(secondKey || {});

            case 'service-providers':
                const { getServiceProvidersByBillType } = await import('@/api/properties/manage_services');
                return () => getServiceProvidersByBillType(secondKey || '');

            case 'deposits':
                const { listDeposits } = await import('@/api/properties/deposits');
                return () => listDeposits(secondKey || {});

            case 'properties':
                const { listProperties: listPropertiesForUsers } = await import('@/api/properties/manage_homes');
                return () => listPropertiesForUsers(secondKey || {});

            case 'utility-admin':
                if (secondKey === 'documents') {
                    const { listDocuments } = await import('@/api/settings/documents');
                    return () => listDocuments(params || {});
                } else if (secondKey === 'spikes') {
                    const { getSpikeAlerts: getUtilityAdminSpikes } = await import('@/api/reports/spike_alerts');
                    return () => getUtilityAdminSpikes(params || {});
                } else if (secondKey === 'trash-bins') {
                    // Note: /api/reports/v1/trash-bins/ endpoint implementation may vary
                    // Returning empty response to prevent 404 errors during preload
                    return async () => {
                        console.warn('Trash bins endpoint does not exist on backend - returning empty data');
                        return { results: [], count: 0 };
                    };
                } else if (secondKey === 'markets') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/reports/v1/markets/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        const result = await response.json();
                        // New reports API returns data in results.markets
                        return result.results?.markets || result;
                    };
                } else if (secondKey === 'thresholds') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/settings/spike-threshold/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        const result = await response.json();
                        return result.success ? result.data : result;
                    };
                } else if (secondKey === 'notifications') {
                    const { getNotificationSettings: getUtilityAdminNotifications } = await import('@/api/settings/notifications');
                    return () => getUtilityAdminNotifications();
                }
                break;

            case 'resident':
                if (secondKey === 'dashboard') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/resident/dashboard/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'billers') {
                    // Note: /api/v1/resident/billers/ endpoint does not exist on backend
                    // Returning empty response to prevent 404 errors during preload
                    return async () => {
                        console.warn('Resident billers endpoint does not exist on backend - returning empty data');
                        return {
                            homeDetails: { address: '', city: '', state: '', zip: '' },
                            billers: [],
                            trashBins: []
                        };
                    };
                } else if (secondKey === 'monthly-statements') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/company/statements/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'payments') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/company/payments/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'pending-cleared') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/transactions/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                } else if (secondKey === 'declined') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/transactions/declined/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                }
                break;

            case 'home-owner':
                if (secondKey === 'overview') {
                    return async () => {
                        const response = await fetch(`${API_BASE_URL}/api/v1/home-owner/dashboard/`, {
                            credentials: 'include', headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                        return response.json();
                    };
                }
                break;

            // Add more cases for other API patterns as needed
            default:
                console.warn(`No query function mapping found for key: ${firstKey}`);
                return null;
        }
    } catch (error) {
        console.error(`Error creating query function for key ${firstKey}:`, error);
        return null;
    }

    return null;
}